import { ArrowUpRight, FileText, Loader2, Plus, Upload } from 'lucide-react'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { SUPPORTED_TYPES } from '@/components/documents/CaseDocumentUploaderView'
import { DocumentThumbnail } from '@/components/identities/DocumentThumbnail'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { AttachIdentityDocumentsState } from '@/features/identities/hooks/useAttachIdentityDocuments'
import type { IdentificationDocument, LinkedDocument } from '@/features/identities/types'

export interface LinkedDocumentsListProps {
  caseId: string
  documents: { linked: LinkedDocument[]; identification: IdentificationDocument[] }
  onNavigateDocument: (documentId: string) => void
  upload: { state: AttachIdentityDocumentsState; onFiles: (files: File[]) => void }
}

function formatSize(bytes?: number): string | null {
  if (bytes == null) return null
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function documentName(doc: LinkedDocument): string {
  if (doc.filename && doc.filename !== 'Unknown') return doc.filename
  const name = doc.properties?.name ?? doc.properties?.document_type
  return typeof name === 'string' && name ? name : '—'
}

function LinkedDocumentTile({
  caseId,
  doc,
  idDoc,
  onNavigateDocument,
}: {
  caseId: string
  doc: LinkedDocument
  idDoc?: IdentificationDocument
  onNavigateDocument: (documentId: string) => void
}) {
  const { t } = useTranslation()
  const name = documentName(doc)
  const category = idDoc
    ? t('identity.detail.linkedDocuments.identification')
    : t('identity.detail.linkedDocuments.other')
  const subtype = idDoc?.documentType ?? doc.properties?.document_type
  return (
    <button
      type="button"
      disabled={!doc.documentId}
      onClick={() => doc.documentId && onNavigateDocument(doc.documentId)}
      className="group flex min-w-0 items-center gap-4 rounded-xl border border-border bg-card p-3 text-left transition-colors hover:border-primary/40 hover:bg-muted/40 disabled:cursor-default"
    >
      <DocumentThumbnail caseId={caseId} doc={doc} />
      <span className="flex min-w-0 flex-1 flex-col items-start gap-2">
        <span className="w-full truncate text-sm font-semibold text-foreground" title={name}>
          {name}
        </span>
        <Badge variant={idDoc ? 'secondary' : 'outline'} className="max-w-full truncate">
          {category}
        </Badge>
        <span className="w-full truncate text-xs text-muted-foreground">
          {[typeof subtype === 'string' ? subtype : null, formatSize(doc.fileSize)]
            .filter(Boolean)
            .join(' · ') || doc.fileType?.split('/').pop()?.toUpperCase()}
        </span>
      </span>
      {doc.documentId && (
        <ArrowUpRight
          className="size-4 shrink-0 self-start text-muted-foreground transition-colors group-hover:text-foreground"
          aria-hidden="true"
        />
      )}
    </button>
  )
}

export function LinkedDocumentsList({
  caseId,
  documents,
  onNavigateDocument,
  upload,
}: LinkedDocumentsListProps) {
  const { t } = useTranslation()
  const input = useRef<HTMLInputElement>(null)
  const busy = upload.state.uploading || upload.state.processing
  const idsBySource = new Map(
    documents.identification
      .filter(doc => doc.sourceDocumentId)
      .map(doc => [doc.sourceDocumentId, doc])
  )

  return (
    <section className="space-y-4" aria-labelledby="linked-documents-heading">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2
            id="linked-documents-heading"
            className="flex items-center gap-2 text-lg font-semibold text-foreground"
          >
            <FileText className="size-5 text-muted-foreground" aria-hidden="true" />
            {t('identity.detail.linkedDocuments.title')}
            <Badge variant="secondary">{documents.linked.length}</Badge>
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('identity.detail.linkedDocuments.subtitle')}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={busy}
          aria-busy={busy}
          onClick={() => input.current?.click()}
        >
          {busy ? (
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          ) : (
            <Upload className="size-4" aria-hidden="true" />
          )}
          {upload.state.uploading
            ? t('identity.detail.addPhoto.uploading', {
                uploaded: upload.state.uploaded,
                total: upload.state.total,
              })
            : upload.state.processing
              ? t('identity.detail.addPhoto.processing')
              : t('identity.detail.linkedDocuments.upload')}
        </Button>
        <input
          ref={input}
          type="file"
          accept={SUPPORTED_TYPES.join(',')}
          multiple
          className="hidden"
          aria-label={t('identity.detail.linkedDocuments.upload')}
          onChange={event => {
            const files = Array.from(event.target.files ?? [])
            event.target.value = ''
            if (files.length) upload.onFiles(files)
          }}
        />
      </div>
      {documents.linked.length === 0 ? (
        <div className="flex flex-col items-center rounded-xl border border-dashed border-border bg-card px-6 py-10 text-center">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-12 rounded-full border-dashed"
            aria-label={t('identity.detail.linkedDocuments.upload')}
            aria-busy={busy}
            disabled={busy}
            onClick={() => input.current?.click()}
          >
            {busy ? (
              <Loader2 className="size-5 animate-spin" aria-hidden="true" />
            ) : (
              <Plus className="size-5" aria-hidden="true" />
            )}
          </Button>
          <p className="mt-3 text-sm font-medium text-foreground">
            {t('identity.detail.linkedDocuments.upload')}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('identity.detail.linkedDocuments.empty')}
          </p>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {documents.linked.map(doc => (
            <LinkedDocumentTile
              key={doc.id}
              caseId={caseId}
              doc={doc}
              idDoc={idsBySource.get(doc.documentId)}
              onNavigateDocument={onNavigateDocument}
            />
          ))}
        </div>
      )}
    </section>
  )
}
